// src/rms/pages/AccessLog.js
// ============================================================================
// Access log — storage facilities
// ============================================================================
// Storage's substitute for usage metering. Instead of "how far did it go", the
// operational question is "who came through the gate, and should they have".
// ============================================================================

import React, { useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import { useCollection, indexById } from "../hooks/useCollection";
import rentalService from "../data/rentalService";
import { PageHeader, Stat, StatusPill, Modal, Field, Select, EmptyState, formatDateTime, assetLabel } from "../components/ui";
import adEngine from "../ads/adEngine";

function AccessLog() {
  const { operator, formatMoney, invalidate, t } = useRms();
  const { rows: events } = useCollection("accessEvents", {}, { orderBy: "occurred_at", direction: "desc" });
  const { rows: credentials } = useCollection("accessCredentials");
  const { rows: assets } = useCollection("assets");
  const { rows: customers } = useCollection("customers");

  const [logging, setLogging] = useState(false);

  const assetsById = useMemo(() => indexById(assets), [assets]);
  const customersById = useMemo(() => indexById(customers), [customers]);

  const liveCreds = credentials.filter((c) => !c.revoked_at);
  const revokedCreds = credentials.filter((c) => c.revoked_at);
  const denials = events.filter((e) => e.event_type === "denied");

  const handleLog = async (draft) => {
    adEngine.recordAction();
    const cred = credentials.find((c) => c.id === draft.credential_id);
    await rentalService.logAccessEvent(operator, {
      assetId: cred?.asset_id,
      occupancyId: cred?.occupancy_id,
      customerId: cred?.customer_id,
      credentialId: draft.credential_id,
      eventType: draft.event_type,
      denialReason: draft.event_type === "denied" ? draft.denial_reason : null,
      source: "manual",
    });
    setLogging(false);
    invalidate();
  };

  return (
    <>
      <PageHeader
        title={t("accessLog.accessLog", "Access log")}
        subtitle={t("accessLog.subtitle", "{events, plural, one {# event} other {# events}} · {creds, plural, one {# live credential} other {# live credentials}}", {
          events: events.length,
          creds: liveCreds.length,
        })}
        actions={
          <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setLogging(true)}>
            {t("accessLog.logAnEvent", "Log an event")}
          </button>
        }
      />

      <div className="rms-stat-row">
        <Stat label={t("accessLog.liveCredentials", "Live credentials")} value={liveCreds.length} />
        <Stat label={t("accessLog.revoked", "Revoked")} value={revokedCreds.length} hint="auto-revoked on move-out" />
        <Stat label={t("accessLog.deniedEntries", "Denied entries")} value={denials.length} />
        <Stat label={t("accessLog.eventsLogged", "Events logged")} value={events.length} />
      </div>

      {events.length === 0 ? (
        <EmptyState
          title={t("accessLog.noAccessEventsYet", "No access events yet")}
          message={t("accessLog.gateActivityAppearsHere", "Gate activity appears here once a keypad integration is connected, or when logged manually.")}
        />
      ) : (
        <div className="rms-table-wrap">
          <table className="rms-table">
            <thead>
              <tr>
                <th>{t("accessLog.when", "When")}</th>
                <th>{t("accessLog.unit", "Unit")}</th>
                <th>{t("accessLog.tenant", "Tenant")}</th>
                <th>{t("accessLog.event", "Event")}</th>
                <th>{t("accessLog.zone", "Zone")}</th>
                <th>{t("accessLog.source", "Source")}</th>
              </tr>
            </thead>
            <tbody>
              {events.slice(0, 100).map((e) => (
                <tr key={e.id}>
                  <td style={{ fontSize: 12.5 }}>{formatDateTime(e.occurred_at)}</td>
                  {/* "Who entered unit B12" is the question this log exists to
                      answer, and B12 is the code — the name alone cannot answer
                      it. */}
                  <td>{assetLabel(assetsById[e.asset_id])}</td>
                  <td>
                    {customersById[e.customer_id]
                      ? `${customersById[e.customer_id].first_name} ${customersById[e.customer_id].last_name || ""}`.trim()
                      : "—"}
                  </td>
                  <td>
                    <StatusPill
                      status={e.event_type === "denied" ? "cancelled" : e.event_type === "entry" ? "active" : "completed"}
                      label={e.event_type}
                    />
                    {e.denial_reason ? (
                      <div style={{ fontSize: 11, color: "var(--rms-danger)", marginTop: 3 }}>{e.denial_reason.replace(/_/g, " ")}</div>
                    ) : null}
                  </td>
                  <td>{e.gate_zone || "—"}</td>
                  <td style={{ color: "var(--rms-muted)", fontSize: 12.5 }}>{e.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="rms-section-title" style={{ marginTop: 26 }}>
        {t("accessLog.credentials", "Credentials")}
      </h2>
      <div className="rms-table-wrap">
        <table className="rms-table">
          <thead>
            <tr>
              <th>{t("accessLog.code", "Code")}</th>
              <th>{t("availability.type", "Type")}</th>
              <th>{t("accessLog.unit", "Unit")}</th>
              <th>{t("accessLog.holder", "Holder")}</th>
              <th>{t("accessLog.lastUsed", "Last used")}</th>
              <th>{t("campaignForm.status", "Status")}</th>
            </tr>
          </thead>
          <tbody>
            {credentials.map((c) => (
              <tr key={c.id}>
                <td>
                  <code>{c.revoked_at ? "••••" : c.credential_value}</code>
                </td>
                <td style={{ color: "var(--rms-muted)" }}>{c.credential_type.replace(/_/g, " ")}</td>
                <td>{assetLabel(assetsById[c.asset_id])}</td>
                <td>
                  {customersById[c.customer_id]
                    ? `${customersById[c.customer_id].first_name} ${customersById[c.customer_id].last_name || ""}`.trim()
                    : "—"}
                </td>
                <td style={{ fontSize: 12.5 }}>{c.last_used_at ? formatDateTime(c.last_used_at) : "never"}</td>
                <td>
                  <StatusPill status={c.revoked_at ? "cancelled" : "active"} label={c.revoked_at ? "revoked" : "live"} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {logging ? (
        <LogEventModal
          credentials={credentials}
          assetsById={assetsById}
          onCancel={() => setLogging(false)}
          onSave={handleLog}
        />
      ) : null}
    </>
  );
}

function LogEventModal({ credentials, assetsById, onCancel, onSave }) {
  const { t } = useRms();
  const [credentialId, setCredentialId] = useState("");
  const [eventType, setEventType] = useState("entry");
  const [denialReason, setDenialReason] = useState("revoked");

  return (
    <Modal
      title={t("accessLog.logAnAccessEvent", "Log an access event")}
      onClose={onCancel}
      width={460}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onCancel}>
            {t("action.cancel", "Cancel")}
          </button>
          <button
            type="button"
            className="rms-btn rms-btn--primary"
            disabled={!credentialId}
            onClick={() => onSave({ credential_id: credentialId, event_type: eventType, denial_reason: denialReason })}
          >
            {t("accessLog.logEvent", "Log event")}
          </button>
        </>
      }
    >
      <Field label={t("accessLog.credential", "Credential")} required>
        <Select
          value={credentialId}
          onChange={setCredentialId}
          placeholder={t("accessLog.chooseCredential", "Choose a credential…")}
          options={credentials.map((c) => ({
            value: c.id,
            label: `${assetLabel(assetsById[c.asset_id], t("accessLog.unit", "Unit"))} — ${c.credential_value}${c.revoked_at ? " (revoked)" : ""}`,
          }))}
        />
      </Field>
      <Field label={t("accessLog.event", "Event")}>
        <Select
          value={eventType}
          onChange={setEventType}
          placeholder={t("accessLog.entry", "Entry")}
          options={[
            { value: "entry", label: "Entry", labelKey: "opt.accessEvent.entry" },
            { value: "exit", label: "Exit", labelKey: "opt.accessEvent.exit" },
            { value: "denied", label: "Denied", labelKey: "opt.accessEvent.denied" },
            { value: "alarm", label: "Alarm", labelKey: "opt.accessEvent.alarm" },
          ]}
        />
      </Field>
      {eventType === "denied" ? (
        <Field label={t("accessLog.reason", "Reason")}>
          <Select
            value={denialReason}
            onChange={setDenialReason}
            placeholder={t("accessLog.revoked", "Revoked")}
            options={[
              { value: "revoked", label: "Credential revoked", labelKey: "opt.denialReason.revoked" },
              { value: "delinquent", label: "Account delinquent", labelKey: "opt.denialReason.delinquent" },
              { value: "unknown_code", label: "Unknown code", labelKey: "opt.denialReason.unknownCode" },
              { value: "outside_hours", label: "Outside access hours", labelKey: "opt.denialReason.outsideHours" },
            ]}
          />
        </Field>
      ) : null}
    </Modal>
  );
}

export default AccessLog;
